import React, { Component } from 'react';
import numeral from 'numeral';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { WithStyles, withStyles } from '@mui/styles';
import _ from 'lodash';
import Cookies from 'js-cookie';

// Contexts
import { withTranslate, withTenant } from '../../core/TenantProvider';
// Components - Atoms, Organisms,Containers
import APDropDownMenu from '../../components/atoms/APDropDownMenu';
import APSection from './../../components/atoms/APSection';
import APSeriesTable from './../../components/organisms/APSeriesTable';
import APCommissionsSection from './../../components/organisms/APCommissionsSection';
import APStatistic from './../../components/atoms/APStatistic';
import APAccountSwitchSection from '../../components/organisms/APAccountSwitchSection/APAccountSwitchSection';
import { generate as xlsx } from './../../components/organisms/APSeriesTable/utils/xlsx';
// Files in the same folder
import AccountContainerStyles from './styles/AccountContainer.style';
import { ReactComponent as SVGSSOSwitchAccountIcon } from './assets/icons/sso-switch-account-icon.svg';
import { ReactComponent as SVGCloseIcon } from './assets/icons/icon-close.svg';
// Types
import { LanguageType, ObjectMappingType } from '../../core/types/GeneralTypes';
// Reducer
import { actions as AccountActions } from './../../redux/modules/account';
import { ReducerState } from './../../redux/modules';
// Utils
import PDFSeriesTable from './../../components/organisms/APSeriesTable/utils/pdf';
import { ColorScale } from './../../core/utils/Chart';
import { GoogleAnalytics4Tags } from '../../core/utils/GoogleAnalyticTag/GoogleAnalytics4Tags';
import { TriggerGoogleAnalytics4Tag, TriggerGoogleAnalyticsTag } from '../../core/utils/GoogleAnalyticTag';
import { GoogleAnalyticTags } from '../../core/utils/GoogleAnalyticTag/GoogleAnalyticsTags';
// Constants
import {
	COMMISSIONS_DETAILED_VIEW,
	COMMISSIONS_SUMMARY_VIEW,
	COMMISSIONS_NONE_VIEW,
	DEFAULT_BREAKPOINT,
	PDF_DOCUMENT_TYPE,
	XLSX_DOCUMENT_TYPE
} from '../../core/utils/Constants/Constants';
import { PDFLink } from './PDFLink';

interface AccountContainerProps extends WithStyles<typeof AccountContainerStyles> {
	getAccountSummary: Function;
	affiliateDetails: any;
	results: any;
	resultsComm: any;
	settings: any;
	translate: any;
	language: LanguageType;
	translations: any;
	location: any;
	forTestingCall?: any;
}

interface AccountContainerState {
	isSSOBannerOpen: boolean;
}

class AccountContainer extends Component<AccountContainerProps, AccountContainerState> {
	table: any;
	state: AccountContainerState;

	constructor(props: AccountContainerProps) {
		super(props);
		this.table = React.createRef();
		this.onDownload = this.onDownload.bind(this);
		this.handleBannerClose = this.handleBannerClose.bind(this);

		const bannerStatusLocalObject: ObjectMappingType = JSON.parse(localStorage.getItem('bannerStatus') || '{}');
		const isSSOBannerOpen: boolean = Boolean(bannerStatusLocalObject?.ssoBanner);

		this.state = {
			isSSOBannerOpen
		};
	}

	componentDidMount() {
		const {
			getAccountSummary,
			affiliateDetails,
			forTestingCall = false
		} = this.props;

		!forTestingCall && getAccountSummary(
			(affiliateDetails?.affiliate?.commission_view === COMMISSIONS_SUMMARY_VIEW ? COMMISSIONS_SUMMARY_VIEW :
				(affiliateDetails?.affiliate?.commission_view === COMMISSIONS_DETAILED_VIEW) ? COMMISSIONS_DETAILED_VIEW : COMMISSIONS_NONE_VIEW), this.props.translate);

		if (Cookies.get('Login.Successful') === undefined || (Cookies.get('Login.Successful') !== 'true')) {
			Cookies.set("Login.Successful", "true");
			TriggerGoogleAnalyticsTag(
				GoogleAnalyticTags.eventAction.load,
				GoogleAnalyticTags.login.load.loginSuccessful
			);
			TriggerGoogleAnalytics4Tag(
				GoogleAnalytics4Tags.login.name,
				GoogleAnalytics4Tags.eventAction.load,
				GoogleAnalytics4Tags.login.load.loginSuccessful
			);
		}
	}

	onDownload(type: string) {
		const {
			affiliateDetails,
			translate
		} = this.props;

		let affiliateName: string = affiliateDetails?.affiliate?.company_name;

		if (type === XLSX_DOCUMENT_TYPE) {
			xlsx(this.props.results.table, 'Account Statement.xlsx', false, translate, affiliateName, 'accountView');
		}
	}

	handleBannerClose = () => {
		this.setState({ isSSOBannerOpen: false });
		const bannerStatusLocalObject: any = JSON.parse(localStorage.getItem('bannerStatus') || '{}');
		localStorage.setItem('bannerStatus', JSON.stringify({
			...bannerStatusLocalObject,
			ssoBanner: false
		}))
	};

	render() {
		const {
			classes,
			results,
			settings,
			translate,
			affiliateDetails
		} = this.props;

		const { isSSOBannerOpen } = this.state;

		if (!results) {
			return null;
		}

		const color = ColorScale();

		const showCommissions: boolean = affiliateDetails?.affiliate?.commission_view &&
			(affiliateDetails?.affiliate?.commission_view === COMMISSIONS_SUMMARY_VIEW ||
				affiliateDetails?.affiliate?.commission_view === COMMISSIONS_DETAILED_VIEW);

		const showAccountSwitchSection: boolean = affiliateDetails?.affiliateRelatedRecords;

		const summariesData = (results && results.summaries) ? results.summaries : {};
		const downloadData = results && results?.table.length > 0 ? results?.table : [];
		const isDownloadDropdownDisabled = downloadData.length <= 0;

		return (
			<div>
				<div className={classes.accountContainer} ref={this.table}>
					<div className={classes.accountContainerContent}>
						{showAccountSwitchSection &&
							(<APSection>
								{isSSOBannerOpen &&
									<div className={classes.ssoBannerContainer}>
										<div className={classes.ssoSwitchAccountIconContainer}>
											<SVGSSOSwitchAccountIcon className={classes.svgSSOSwitchAccountIcon} />
										</div>
										{translate('account.section.accountswitch.notification.text')}
										<SVGCloseIcon className={classes.svgIconClose}
											onClick={this.handleBannerClose} />
									</div>
								}
								<APAccountSwitchSection affiliateDetails={affiliateDetails} />
							</APSection>)
						}
						<APSection
							className={classes.accountSectionSummary}
							gaTagParam={GoogleAnalyticTags.accounts.click.summaryViewDetails}
							ga4TagParam={GoogleAnalytics4Tags.accounts.click.summaryViewDetails}
							ga4TagModule={GoogleAnalytics4Tags.accounts.name}
							title={translate('account.section.accountsummary.title')}
							link={'/referrals'} linkText={translate('global.view.details')}
						>
							<div className={classes.statistics}>
								{summariesData && (
									Object.keys(summariesData).map((summary: any, index) => (
										<APStatistic
											color={color(summary)}
											name={translate(`account.statistics.${summary}.name`)}
											summary={translate(`account.statistics.${summary}.summary`)}
											value={numeral(summariesData[summary]).format('0,000')}
											key={`key-${index}`}
										/>
									))
								)}
							</div>
						</APSection>

						{/* Commission generated */}
						{showCommissions &&
							<APSection
								className={classes.accountSection}
								gaTagParam={GoogleAnalyticTags.accounts.click.commissionsGeneratedViewDetails}
								ga4TagParam={GoogleAnalytics4Tags.accounts.click.commissionsGeneratedViewDetails}
								ga4TagModule={GoogleAnalytics4Tags.accounts.name}
								title={translate('account.section.commission.title')}
								link={'/commissions'}
								linkText={translate('global.view.details')}
							>
								<APCommissionsSection baseCurrency={affiliateDetails?.affiliate?.default_currency} />
							</APSection>
						}

						<APSection shadow={false} padded={false} title={translate('account.section.annualsummary.title')}>
							{results && results.table && (
								<>
									<APDropDownMenu
										className={classes.dropDownMenu}
										label={translate('account.section.annualsummary.download.button')}
										onChange={this.onDownload}
										options={[
											{ label: <PDFLink {...this.props} />, value: PDF_DOCUMENT_TYPE },
											{ label: "XLSX", value: XLSX_DOCUMENT_TYPE }
										]}
										hasDownloadIcon={true}
										disabled={isDownloadDropdownDisabled}
									/>
									<APSeriesTable breakPoint={DEFAULT_BREAKPOINT} series={results.table} />
								</>
							)}
						</APSection>

					</div>
				</div>
			</div >
		);
	}
}

const mapStateToProps = (state: ReducerState) => ({
	results: state.account.results,
	resultsComm: state.account.resultsComm,
	affiliateDetails: state.user.affiliateDetails
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	getAccountSummary: AccountActions.getAccountSummary
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(
	withTenant(
		withTranslate(
			withStyles(AccountContainerStyles)(AccountContainer)
		)
	)
);